import React, { useEffect, useRef, useState } from "react";
import { Map, Marker, Polyline, GoogleApiWrapper } from "google-maps-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCircle,
  FaFlagCheckered,
  FaClock,
  FaRoute,
} from "react-icons/fa";
import { connectTrackingSocket, getTrackingHost } from "../../redux/config/trackingSocket";
import { IWM } from "../../redux/config/urlConfig";

// Same key used by the existing Google maps in this app (see pages/.../Map.js).
const GOOGLE_MAPS_API_KEY = "AIzaSyDsVU8VuCB3p7REGp24SbXZnkNXHaIKCfo";

// Zoom level the map follows the driver at. Close enough to read streets while
// still showing the surrounding area.
const FOLLOW_ZOOM = 16;

// Fallback center (approx. centroid of India) used before the first fix lands.
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };

const containerStyle = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
};

// Build a Google Maps marker icon from inline SVG (no extra network request).
const svgMarker = (google, svg, size, anchor) => ({
  url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
  scaledSize: new google.maps.Size(size, size),
  anchor: new google.maps.Point(anchor[0], anchor[1]),
});

// Truck-in-a-pin icon for the live driver position.
const driverIconSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
  <circle cx="23" cy="23" r="20" fill="#27AE60" stroke="#fff" stroke-width="3"/>
  <g transform="translate(11,15)" fill="#fff">
    <rect x="0" y="0" width="14" height="10" rx="1"/>
    <path d="M14 3h4.5L23 7v3h-9z"/>
    <circle cx="5" cy="11.5" r="2.6" fill="#1b5e20"/>
    <circle cx="18" cy="11.5" r="2.6" fill="#1b5e20"/>
  </g>
</svg>`;

// Checkered-flag pin for the trip destination (drop location).
const dropIconSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <circle cx="20" cy="20" r="17" fill="#e74c3c" stroke="#fff" stroke-width="3"/>
  <g transform="translate(13,11)" fill="#fff">
    <rect x="0" y="0" width="2" height="18"/>
    <path d="M2 1h12v8H2z" fill="#fff"/>
    <path d="M2 1h3v2H2zM8 1h3v2H8zM5 3h3v2H5zM11 3h3v2h-3zM2 5h3v2H2zM8 5h3v2H8zM5 7h3v2H5zM11 7h3v2h-3z" fill="#e74c3c"/>
  </g>
</svg>`;

function TrackTrip({ google }) {
  const { id: pickupId } = useParams();
  const navigate = useNavigate();

  const socketRef = useRef(null);
  const mapRef = useRef(null);
  const didInitialFitRef = useRef(false);
  const dropPointRef = useRef(null);
  const lastEtaAtRef = useRef(0);

  const [location, setLocation] = useState(null); // { latitude, longitude, ... }
  const [status, setStatus] = useState("connecting"); // connecting | online | offline
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [eta, setEta] = useState(null); // { durationText, distanceText }

  // Destination (drop) coordinates, if the trip carries them.
  const drop = trip?.dropLocation;
  const dropPoint =
    typeof drop?.latitude === "number" && typeof drop?.longitude === "number"
      ? { lat: drop.latitude, lng: drop.longitude }
      : null;

  // Keep the latest drop point in a ref so map callbacks can read it without
  // being re-created on every render.
  useEffect(() => {
    dropPointRef.current = dropPoint;
  }, [dropPoint]);

  // On the first fix, frame both the driver and the destination; after that,
  // follow the driver so the map tracks movement.
  const followDriver = (loc) => {
    if (!mapRef.current || !loc) return;
    const map = mapRef.current;
    const driverLatLng = { lat: loc.latitude, lng: loc.longitude };

    if (!didInitialFitRef.current && dropPointRef.current && google) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(driverLatLng);
      bounds.extend(dropPointRef.current);
      map.fitBounds(bounds);
      didInitialFitRef.current = true;
      return;
    }

    map.panTo(driverLatLng);
    if (map.getZoom() < FOLLOW_ZOOM) map.setZoom(FOLLOW_ZOOM);
  };

  // Estimate remaining drive time/distance from the driver to the drop point.
  // Throttled to once every 15s so frequent location pings don't hammer the API.
  useEffect(() => {
    if (!google || !location || !dropPoint) return;
    const now = Date.now();
    if (eta && now - lastEtaAtRef.current < 15000) return;
    lastEtaAtRef.current = now;

    const service = new google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [{ lat: location.latitude, lng: location.longitude }],
        destinations: [dropPoint],
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (res, reqStatus) => {
        if (reqStatus !== "OK") return;
        const el = res?.rows?.[0]?.elements?.[0];
        if (el?.status === "OK") {
          setEta({
            durationText: el.duration?.text,
            distanceText: el.distance?.text,
          });
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, location, drop?.latitude, drop?.longitude]);

  // Pull trip metadata (driver, route) for the header. Best-effort.
  useEffect(() => {
    let active = true;
    IWM.get(`/pickups/read/${pickupId}`)
      .then((res) => active && setTrip(res.data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pickupId]);

  // Subscribe to the live driver feed for this pickup.
  useEffect(() => {
    const socket = connectTrackingSocket();
    socketRef.current = socket;

    // Guard so a transient connect_error during reconnection doesn't spam the
    // console or stomp the UI on every retry — we only surface it once.
    let connectErrorLogged = false;

    const handleConnect = () => {
      // A successful (re)connection clears any prior connection error.
      connectErrorLogged = false;
      setError(null);
      socket.emit("dashboard:track:subscribe", { pickupId }, (res) => {
        if (res?.status !== "ok") {
          setError(res?.message || "Unable to track this trip");
          return;
        }
        if (res.lastLocation) {
          // Render an initial marker without waiting for the next ping.
          setLocation(res.lastLocation);
          setStatus("offline");
          followDriver(res.lastLocation);
        } else {
          setStatus("offline");
        }
      });
    };

    const handleLocation = (loc) => {
      if (loc.pickupId && loc.pickupId !== pickupId) return;
      setStatus("online");
      setLocation(loc);
      followDriver(loc);
    };

    const handleConnectError = (err) => {
      // socket.io retries automatically (bounded in connectTrackingSocket); log
      // only the first failure and only the message, never the full error
      // object — re-logging it every attempt is what bloats Chrome's memory.
      if (!connectErrorLogged) {
        connectErrorLogged = true;
        console.warn("Tracking socket connect_error:", err?.message);
      }
    };

    const handleReconnectFailed = () => {
      // Reconnection budget exhausted; stop and tell the user once.
      setError(
        `Could not connect to the tracking server (${getTrackingHost()}).`
      );
    };

    socket.on("connect", handleConnect);
    socket.on("driver:location", handleLocation);
    socket.on("driver:online", () => setStatus("online"));
    socket.on("driver:offline", () => setStatus("offline"));
    socket.on("connect_error", handleConnectError);
    socket.io.on("reconnect_failed", handleReconnectFailed);

    return () => {
      if (socket.connected) socket.emit("dashboard:track:unsubscribe", { pickupId });
      // Drop every listener and tear the connection down so nothing keeps
      // firing (or retrying) after the page unmounts.
      socket.off();
      socket.io.off("reconnect_failed", handleReconnectFailed);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupId]);

  const center = location
    ? { lat: location.latitude, lng: location.longitude }
    : DEFAULT_CENTER;

  const statusMeta = {
    connecting: { color: "#f1c40f", label: "Connecting…" },
    online: { color: "#27AE60", label: "Driver live" },
    offline: { color: "#e74c3c", label: "Driver offline" },
  }[status];

  const driver = trip?.drivers?.[0];

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
      {/* Header overlay */}
      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          zIndex: 5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
            pointerEvents: "auto",
            maxWidth: 360,
          }}
        >
          <div className="flex align-center" style={{ gap: 10 }}>
            <FaArrowLeft
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer", color: "#555" }}
              title="Back"
            />
            <span className="bold">Live driver tracking</span>
          </div>
          {trip && (
            <div style={{ marginTop: 8, fontSize: 13, color: "#555" }}>
              {driver?.name && (
                <div>
                  Driver: <span className="bold">{driver.name}</span>
                  {driver.phoneNumber ? ` · ${driver.phoneNumber}` : ""}
                </div>
              )}
              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                {trip.generator?.name || trip.pickupLocation?.address || "Pickup"}
                {" → "}
                {trip.contradictingOrg?.name ||
                  trip.processor?.name ||
                  trip.dropLocation?.address ||
                  "Drop"}
              </div>
              {/* End-trip (destination) location */}
              <div
                className="flex align-center"
                style={{ gap: 6, color: "#e74c3c", fontSize: 12, marginTop: 6 }}
              >
                <FaFlagCheckered style={{ fontSize: 12 }} />
                <span className="bold" style={{ color: "#555" }}>
                  Drop:
                </span>
                <span style={{ color: "#777" }}>
                  {trip.dropLocation?.nickname ||
                    trip.dropLocation?.address ||
                    "Destination"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 8,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 999,
              padding: "8px 14px",
              boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
            }}
          >
            <FaCircle style={{ color: statusMeta.color, fontSize: 10 }} />
            <span className="bold">{statusMeta.label}</span>
          </div>

          {/* Estimated time / distance for the driver to reach the drop */}
          {eta && (
            <div
              style={{
                background: "#fff",
                borderRadius: 999,
                padding: "8px 14px",
                boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
                pointerEvents: "auto",
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontSize: 13,
              }}
            >
              <span className="flex align-center" style={{ gap: 6 }}>
                <FaClock style={{ color: "#27AE60", fontSize: 12 }} />
                <span className="bold">{eta.durationText}</span>
              </span>
              {eta.distanceText && (
                <span
                  className="flex align-center"
                  style={{ gap: 6, color: "#777" }}
                >
                  <FaRoute style={{ fontSize: 12 }} />
                  {eta.distanceText}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Speed / accuracy readout */}
      {location && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 16,
            zIndex: 5,
            background: "#fff",
            borderRadius: 12,
            padding: "10px 14px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
            fontSize: 12,
            color: "#555",
          }}
        >
          {typeof location.speed === "number" && (
            <div>Speed: {(location.speed * 3.6).toFixed(1)} km/h</div>
          )}
          {typeof location.accuracy === "number" && (
            <div>Accuracy: ±{Math.round(location.accuracy)} m</div>
          )}
          {location.updatedAt && (
            <div style={{ color: "#999" }}>
              Updated {new Date(location.updatedAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 6,
            background: "#fff",
            borderRadius: 12,
            padding: "16px 20px",
            boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
            color: "#e74c3c",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          {error}
        </div>
      )}

      {!location && !error && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5,
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            borderRadius: 999,
            padding: "8px 16px",
            fontSize: 13,
          }}
        >
          Waiting for the driver's location…
        </div>
      )}

      <Map
        google={google}
        zoom={FOLLOW_ZOOM}
        initialCenter={center}
        center={center}
        containerStyle={containerStyle}
        onReady={(_, map) => {
          mapRef.current = map;
          if (location) followDriver(location);
        }}
        streetViewControl={false}
        mapTypeControl={false}
        fullscreenControl={false}
      >
        {/* Route line from the driver to the destination */}
        {location && dropPoint && (
          <Polyline
            path={[
              { lat: location.latitude, lng: location.longitude },
              dropPoint,
            ]}
            options={{
              strokeColor: "#27AE60",
              strokeOpacity: 0.9,
              strokeWeight: 4,
              geodesic: true,
            }}
          />
        )}

        {/* Destination (end-trip) marker */}
        {dropPoint && (
          <Marker
            title={
              trip?.dropLocation?.nickname ||
              trip?.dropLocation?.address ||
              "Drop location"
            }
            position={dropPoint}
            icon={svgMarker(google, dropIconSvg, 40, [20, 20])}
          />
        )}

        {/* Live driver position */}
        {location && (
          <Marker
            title={driver?.name || "Driver"}
            position={{ lat: location.latitude, lng: location.longitude }}
            icon={svgMarker(google, driverIconSvg, 46, [23, 23])}
          />
        )}
      </Map>
    </div>
  );
}

export default GoogleApiWrapper({
  apiKey: GOOGLE_MAPS_API_KEY,
})(TrackTrip);
